import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { apiAdmin as api } from "@/lib/api";
import { reportError } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EzLogo } from "@/pages/LandingPage";
import { Lock, ArrowRight, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    setLoading(true);
    try {
      const res = await api.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("ez_admin_token", res.data.token);
      localStorage.setItem("ez_admin_user", res.data.username);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      reportError(err, err.response?.data?.detail || "Login failed", true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ez-surface flex flex-col" data-testid="admin-login-page">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
          <EzLogo size="text-xl" />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-ez-teal flex items-center justify-center">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-ez-teal font-heading" data-testid="login-title">Admin Login</h1>
                <p className="text-sm text-slate-500 font-body">Access the application dashboard</p>
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block font-body">Username</label>
                <Input
                  data-testid="input-username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="admin"
                  autoComplete="username"
                  className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-base"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block font-body">Password</label>
                <div className="relative">
                  <Input
                    data-testid="input-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-base pr-12"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-btn"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading || !username.trim() || !password.trim()}
                className="w-full rounded-xl bg-ez-teal text-white hover:bg-[#3D5051] h-12 text-base font-semibold shadow-lg shadow-slate-200 hover:-translate-y-0.5 active:translate-y-0 transition-transform"
              >
                {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6 font-body">
            This area is restricted to Ez Health administrators.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
